/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveLine } from '@nivo/line';

import CustomLegend from '../../utils/chartTools/createLegend';
import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import colorSelector from '../../utils/formatCharts/handleColor';
import customAxis from '../../utils/formatCharts/formatAxis';
import { labelValues } from '../../utils/formatCharts/handleCustomValue';
import { genData2DWithPoints, dataFiller } from '../../utils/formatCharts/handleData';
import { getBiggestTick, getSmallestTick } from '../../utils/formatCharts/getAxisTicks';
// import { checkErrors } from '../../utils/chartTools/kpiMsg';
import KpiStatus from '../../utils/chartTools/KpiStatus';
import CustomLabel from './CustomLabel';

const emptyObj = {};

const Line = ({
  data, styleConfig, isEdit, globalConfig = emptyObj, handleDrilldownClick = null,
}) => {
  const [chartData, setChartData] = useState({ ready: false });
  const [filteredChartData, setFilteredChartData] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);

  useEffect(() => {
    setChartData(genData2DWithPoints(dataFiller(data, styleConfig), styleConfig, true));
  }, [
    data,
    styleConfig.DataFormat,
    styleConfig.DataPercentFormat,
    styleConfig.DataFormatAlt,
    styleConfig.DataPercentFormatAlt,
    styleConfig.DataAbsoluteFormatAlt,
    styleConfig.TotalLineControl,
    styleConfig.InvertVariation,
    styleConfig.ExtendDateColumn,
    styleConfig.ExtendDateLine,
    ...styleConfig.CustomSortControl.kpiCategories,
    styleConfig.SortValuesControl,
    styleConfig.CustomSortColumnControl?.kpiCategories,
    styleConfig.SortColumnsControl,
  ]);

  useEffect(() => {
    if (chartData.ready) {
      setFilteredChartData(chartData.data.filter((item) => !hiddenIds.includes(item.id)));
    }
  }, [chartData, hiddenIds]);

  const biggestTick = useMemo(() => (
    getBiggestTick(chartData.biggest, styleConfig)
  ), [chartData]);
  const smallestTick = useMemo(() => (
    getSmallestTick(chartData.smallest, styleConfig)
  ), [chartData]);

  const createLabel = (label, x) => {
    let offset = -15;
    let val = '';
    const [valIni, valEnd] = labelValues(label.value, label.value2, styleConfig, label.x);
    if (styleConfig.LabelLineBreak && styleConfig?.DataFormatAlt !== 'none') {
      val = (
        <>
          <tspan>{valIni}</tspan>
          <tspan x={x} dy="1.2em">{valEnd}</tspan>
        </>
      );
      offset = -28;
    } else val = `${valIni} ${valEnd}`.trim();
    return <tspan y={offset}>{val}</tspan>;
  };

  const createTooltip = (val) => customTooltip(val, 'line', styleConfig, handleDrilldownClick);

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          'NoLineSelected',
          data?.index?.[0] === 'Totais',
        ],
      ])}
    >
      <ResponsiveLine
        data={filteredChartData}
        /* GENERAL */
        onClick={handleDrilldownClick ? ((dataPoint) => {
          handleDrilldownClick(dataPoint?.points[0]?.data?.id);
        }) : null}
        useMesh={styleConfig.InteractiveControl ?? true}
        lineWidth={styleConfig.LineWidth}
        margin={
          styleConfig?.Margin || {
            top: 50,
            right: 130,
            bottom: 50,
            left: 60,
          }
        }
        /* POINTS */
        enablePoints={styleConfig?.EnablePointsControl?.checked ?? false}
        pointSize={styleConfig?.EnablePointsControl?.pointsSize || 10}
        pointBorderWidth={Number(styleConfig?.EnablePointsControl?.border) || 2}
        // eslint-disable-next-line no-nested-ternary
        pointColor={styleConfig?.PointsColorControl ? (
          typeof styleConfig.PointsColorControl === 'string' ? styleConfig?.PointsColorControl : {
            from: 'series.color', modifiers: styleConfig?.PointsColorControl?.modifiers ?? [],
          }
        ) : { theme: 'background' }}
        pointBorderColor={{ from: 'seriesColor', modifiers: [] }}
        enableGridX={styleConfig?.EnableGridX ?? false}
        enableGridY={styleConfig?.EnableGridY ?? false}
        /* AREA */
        areaOpacity={styleConfig?.AreaControl?.opacity || 0.2}
        enableArea={styleConfig?.AreaControl?.checked || false}
        curve={styleConfig?.LinesFormatControl || 'linear'}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        /* LABEL */
        enablePointLabel={false}
        // pointLabel={createLabel}
        labelSkipWidth={12}
        labelSkipHeight={12}
        /* TOOLTIP */
        isInteractive={styleConfig.InteractiveControl}
        sliceTooltip={({ slice }) => slice.points.map(createTooltip)}
        enableSlices="x"
        /* AXIS */
        axisTop={customAxis('top', styleConfig, globalConfig, false)}
        axisLeft={customAxis('left', styleConfig, globalConfig)}
        axisBottom={customAxis('bottom', styleConfig, globalConfig, false)}
        axisRight={customAxis('right', styleConfig, globalConfig)}
        legends={styleConfig?.LegendControl?.checked ? [{
          ...styleConfig?.LegendPlacementControl?.value,
          itemWidth: 100,
          itemHeight: 18,
          symbolSize: 18,
          symbolShape: styleConfig?.LegendSymbolControl || 'circle',
          itemsSpacing: parseInt(styleConfig?.LegendsSpacingControl?.value, 10),
          effects: [{ on: 'hover', style: { itemTextColor: '#000' } }],
          toggleSerie: !isEdit,
        }] : []}
        layers={[
          'grid',
          'markers',
          'axes',
          'areas',
          'crosshair',
          'lines',
          'points',
          (dt) => (styleConfig.EnableLabelControl?.checked === false ? null : CustomLabel({
            data: dt.points, styleConfig, content: createLabel, globalConfig,
          })),
          'slices',
          'mesh',
          styleConfig?.LegendControl?.checked ? (dt) => (
            <CustomLegend
              chartDataAr={chartData.data}
              colorParts={dt.series.reduce((aux, p) => {
                aux[p.id] = p.color;
                return aux;
              }, {})}
              sizes={{
                width: dt.innerWidth,
                height: dt.innerHeight,
              }}
              config={styleConfig}
              setToggleIds={setHiddenIds}
              isEdit={isEdit}
            />
          ) : null,
        ]}
        yScale={{
          type: 'linear',
          stacked: false,
          min: styleConfig?.MinValueControl?.checked
            ? smallestTick
            : styleConfig?.MinValueControl?.value || 0,
          max: styleConfig?.MaxValueControl?.checked
            ? biggestTick
            : styleConfig?.MaxValueControl?.value || 0,
        }}
        /* STYLES/THEMES */
        colors={colorSelector({
          dataLength: chartData.data.length,
          config: styleConfig,
          colorKey: 'id',
          globalConfig,
        })}
        theme={{
          axis: {
            legend: {
              text: {
                fontSize: styleConfig?.AxisGlobal?.legendFontSize,
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig?.AxisGlobal?.fontFamily}', Arial`,
                fill: styleConfig?.AxisGlobal?.color,
              },
            },
            ticks: {
              text: {
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig?.AxisGlobal?.fontFamily}', Arial`,
                fontSize: styleConfig?.AxisGlobal?.fontSize,
                fill: styleConfig?.AxisGlobal?.color,
              },
            },
          },
          dots: {
            text: {
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig?.DotsLabel?.fontFamily}', Arial`,
              fontSize: styleConfig?.DotsLabel?.fontSize,
              fill: styleConfig?.DotsLabel?.color,
              transform: `translate(${styleConfig?.LabelTranslateX ? styleConfig?.LabelTranslateX : 0}px, ${styleConfig?.LabelTranslateY ? styleConfig?.LabelTranslateY : 0}px) rotate(${styleConfig?.LabelRotationControl ? styleConfig?.LabelRotationControl : 0}deg)`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          legends: {
            text: {
              fontSize: `${styleConfig?.LegendsFontSizeControl?.value}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLegend : styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
        }}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
        markers={styleConfig.ShowMarkerControl
          ? styleConfig.MarkersList?.reduce((aux, lines) => {
            if (lines.showLine) {
              aux.push({
                ...lines,
                textStyle: {
                  ...lines.textStyle,
                  fontFamily: `'${lines.textStyle.fontFamily}', Arial`,
                },
              });
            }
            return aux;
          }, []) : []}
      />
    </KpiStatus>
  ) : <></>;
};

Line.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  isEdit: PropTypes.bool.isRequired,
  globalConfig: PropTypes.objectOf(PropTypes.any),
  handleDrilldownClick: PropTypes.func,
};

export default Line;
